import { useCallback, useRef } from 'react'

/**
 * A custom React hook that returns a stable callback function reference.
 *
 * This hook wraps a callback function in a `useCallback` hook with an empty dependency array,
 * ensuring that the returned function reference remains constant between renders. However,
 * it internally uses a mutable ref to always invoke the most recent version of the callback.
 *
 * This is particularly useful when you need to pass a callback to child components or third-party
 * libraries that depend on a stable function reference, while still ensuring that the latest logic
 * is executed.
 *
 * @template T - The function type of the callback.
 * @param {T} callback - The callback function to be wrapped.
 * @returns {T} A stable function reference that always calls the latest version of the callback.
 *
 * @example
 * const handleClick = useCallbackRef((event) => {
 *   // This will always use the most recent logic from the callback
 *   console.log('Button clicked:', event);
 * });
 */
const useCallbackRef = <T extends (...args: unknown[]) => unknown>(
  callback: T,
): T => {
  const callbackRef = useRef<T>(callback)
  callbackRef.current = callback

  return useCallback(
    ((...args) => {
      return callbackRef.current?.(...args)
    }) as T,
    [],
  )
}

export default useCallbackRef
